import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import ErrorBoundary from "@/components/ErrorBoundary";
import LoginPage from "@/pages/Login";
import DashboardLayout from "@/components/DashboardLayout";
import Dashboard from "@/pages/Dashboard";
import OzonProfiles from "@/pages/OzonProfiles";
import BrowserProfiles from "@/pages/BrowserProfiles";
import Tasks from "@/pages/Tasks";
import BetboomTemplate from "@/pages/BetboomTemplate";
import BetboomPreview from "@/pages/BetboomPreview";
import BetboomPaymentsPreview from "@/pages/BetboomPaymentsPreview";
import CashinoutTemplate from "@/pages/CashinoutTemplate";
import CashinoutPreview from "@/pages/CashinoutPreview";
import CashinoutPaymentsPreview from "@/pages/CashinoutPaymentsPreview";
import CashinoutMainPreview from "@/pages/CashinoutMainPreview";
import CashinoutCombinedPreview from "@/pages/CashinoutCombinedPreview";
import AdminUsers from "@/pages/AdminUsers";
import AdminNotifications from "@/pages/AdminNotifications";
import AdminIntegrations from "@/pages/AdminIntegrations";
import Chat from "@/pages/Chat";
import TempEmail from "@/pages/TempEmail";
import Receipts from "@/pages/Receipts";
import Certificates from "@/pages/Certificates";
import NovapayTemplate from "@/pages/NovapayTemplate";
import NovapayMain from "@/pages/NovapayMain";
import NovapayPayments from "@/pages/NovapayPayments";
import NovapayPersonal from "@/pages/NovapayPersonal";
import BybitTemplate from "@/pages/BybitTemplate";
import BybitMain from "@/pages/BybitMain";
import BybitTrades from "@/pages/BybitTrades";
import BybitProfile from "@/pages/BybitProfile";
import BybitWallet from "@/pages/BybitWallet";
import LigaStavokTemplate from "@/pages/LigaStavokTemplate";
import LigaStavokMain from "@/pages/LigaStavokMain";
import LigaStavokHistory from "@/pages/LigaStavokHistory";
import LigaStavokProfile from "@/pages/LigaStavokProfile";
import LigaStavokWallet from "@/pages/LigaStavokWallet";
import { PlatiPomiruTemplate, PlatiPomiruMain, PlatiPomiruHistory, PlatiPomiruProfile } from "@/pages/PlatiPomiru";
import Statistics from "@/pages/Statistics";
import PersonalData from "@/pages/PersonalData";
import Drawing from "@/pages/Drawing";
import SEOSettings from "@/pages/SEOSettings";
import ProxySettings from "@/pages/ProxySettings";
import ExtensionSettings from "@/pages/ExtensionSettings";
import WorkSchedule from "@/pages/WorkSchedule";

// New templates - Crypto Exchanges
import CoinPayTemplate from "@/pages/CoinPayTemplate";
import CoinPayMain from "@/pages/CoinPayMain";
import CoinPayHistory from "@/pages/CoinPayHistory";
import CoinPaySettings from "@/pages/CoinPaySettings";
import CoinPayCombinedPreview from "@/pages/CoinPayCombinedPreview";
import CryptoTradeTemplate from "@/pages/CryptoTradeTemplate";
import CryptoTradeMain from "@/pages/CryptoTradeMain";
import CryptoTradeHistory from "@/pages/CryptoTradeHistory";
import CryptoTradeCombinedPreview from "@/pages/CryptoTradeCombinedPreview";
import BitExchangeTemplate from "@/pages/BitExchangeTemplate";
import BitExchangeMain from "@/pages/BitExchangeMain";
import BitExchangeHistory from "@/pages/BitExchangeHistory";
import BitExchangeSettings from "@/pages/BitExchangeSettings";
import BitExchangeCombinedPreview from "@/pages/BitExchangeCombinedPreview";

// New templates - Casinos
import VulkanBetTemplate from "@/pages/VulkanBetTemplate";
import VulkanBetCombinedPreview from "@/pages/VulkanBetCombinedPreview";
import JoyCasinoTemplate from "@/pages/JoyCasinoTemplate";
import JoyCasinoMain from "@/pages/JoyCasinoMain";
import JoyCasinoCombinedPreview from "@/pages/JoyCasinoCombinedPreview";
import StardaCombinedPreview from "@/pages/StardaCombinedPreview";
import OneXStavkaTemplate from "@/pages/OneXStavkaTemplate";
import OneXStavkaMain from "@/pages/OneXStavkaMain";
import OneXStavkaCombinedPreview from "@/pages/OneXStavkaCombinedPreview";
import OneWinTemplate from "@/pages/OneWinTemplate";
import OneWinCombinedPreview from "@/pages/OneWinCombinedPreview";
import TGCashinoutTemplate from "@/pages/TGCashinoutTemplate";
import TGCashinoutPreview from "@/pages/TGCashinoutPreview";
import TGWebApp from "@/pages/TGWebApp";
import useSEO from "@/hooks/useSEO";

// Auth check helper
const isAuthenticated = () => {
  const token = localStorage.getItem("token");
  return !!token;
};

// Admin check helper
const isAdmin = () => {
  return localStorage.getItem("role") === "admin";
};

// Protected Route component
const ProtectedRoute = ({ children }) => {
  if (!isAuthenticated()) {
    return <Navigate to="/login" replace />;
  }
  return children;
};

// Admin Route component
const AdminRoute = ({ children }) => {
  if (!isAuthenticated()) {
    return <Navigate to="/login" replace />;
  }
  if (!isAdmin()) {
    return <Navigate to="/" replace />;
  }
  return children;
};

function App() {
  // Apply SEO settings
  useSEO();
  
  return (
    <div className="App">
      <BrowserRouter>
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/templates/betboom/preview" element={<BetboomPreview />} />
          <Route path="/templates/betboom/payments" element={<BetboomPaymentsPreview />} />
          <Route path="/templates/cashinout/preview" element={<CashinoutPreview />} />
          <Route path="/templates/cashinout/payments" element={<CashinoutPaymentsPreview />} />
          <Route path="/templates/cashinout/main" element={<CashinoutMainPreview />} />
          <Route path="/templates/cashinout/combined" element={<CashinoutCombinedPreview />} />
          <Route path="/templates/novapay/main" element={<NovapayMain />} />
          <Route path="/templates/novapay/payments" element={<NovapayPayments />} />
          <Route path="/templates/novapay/personal" element={<NovapayPersonal />} />
          <Route path="/templates/bybit/main" element={<BybitMain />} />
          <Route path="/templates/bybit/trades" element={<BybitTrades />} />
          <Route path="/templates/bybit/wallet" element={<BybitWallet />} />
          <Route path="/templates/bybit/profile" element={<BybitProfile />} />
          <Route path="/templates/ligastavok/main" element={<LigaStavokMain />} />
          <Route path="/templates/ligastavok/history" element={<LigaStavokHistory />} />
          <Route path="/templates/ligastavok/wallet" element={<LigaStavokWallet />} />
          <Route path="/templates/ligastavok/profile" element={<LigaStavokProfile />} />
          <Route path="/templates/platipomiru/main" element={<PlatiPomiruMain />} />
          <Route path="/templates/platipomiru/history" element={<PlatiPomiruHistory />} />
          <Route path="/templates/platipomiru/profile" element={<PlatiPomiruProfile />} />
          
          {/* CoinPay Crypto Exchange */}
          <Route path="/templates/coinpay/main" element={<CoinPayMain />} />
          <Route path="/templates/coinpay/history" element={<CoinPayHistory />} />
          <Route path="/templates/coinpay/settings" element={<CoinPaySettings />} />
          <Route path="/templates/coinpay/combined" element={<CoinPayCombinedPreview />} />
          
          {/* CryptoTrade Exchange */}
          <Route path="/templates/cryptotrade/main" element={<CryptoTradeCombinedPreview />} />
          <Route path="/templates/cryptotrade/history" element={<CryptoTradeCombinedPreview />} />
          <Route path="/templates/cryptotrade/settings" element={<CryptoTradeCombinedPreview />} />
          <Route path="/templates/cryptotrade/combined" element={<CryptoTradeCombinedPreview />} />
          
          {/* BitExchange P2P */}
          <Route path="/templates/bitexchange/main" element={<BitExchangeMain />} />
          <Route path="/templates/bitexchange/history" element={<BitExchangeHistory />} />
          <Route path="/templates/bitexchange/settings" element={<BitExchangeSettings />} />
          <Route path="/templates/bitexchange/combined" element={<BitExchangeCombinedPreview />} />
          
          {/* VulkanBet Casino */}
          <Route path="/templates/vulkanbet/main" element={<VulkanBetCombinedPreview />} />
          <Route path="/templates/vulkanbet/history" element={<VulkanBetCombinedPreview />} />
          <Route path="/templates/vulkanbet/settings" element={<VulkanBetCombinedPreview />} />
          <Route path="/templates/vulkanbet/combined" element={<VulkanBetCombinedPreview />} />
          
          {/* JoyCasino */}
          <Route path="/templates/joycasino/main" element={<JoyCasinoMain />} />
          <Route path="/templates/joycasino/combined" element={<JoyCasinoCombinedPreview />} />
          <Route path="/templates/joycasino/history" element={<JoyCasinoCombinedPreview />} />
          <Route path="/templates/joycasino/settings" element={<JoyCasinoCombinedPreview />} />
          <Route path="/templates/joycasino/profile" element={<JoyCasinoCombinedPreview />} />
          
          {/* Starda Casino */}
          <Route path="/templates/starda/main" element={<StardaCombinedPreview />} />
          <Route path="/templates/starda/history" element={<StardaCombinedPreview />} />
          <Route path="/templates/starda/settings" element={<StardaCombinedPreview />} />
          <Route path="/templates/starda/profile" element={<StardaCombinedPreview />} />
          <Route path="/templates/starda/combined" element={<StardaCombinedPreview />} />
          
          {/* 1xStavka */}
          <Route path="/templates/1xstavka/main" element={<OneXStavkaMain />} />
          <Route path="/templates/1xstavka/combined" element={<OneXStavkaCombinedPreview />} />
          
          {/* 1Win */}
          <Route path="/templates/1win/main" element={<OneWinCombinedPreview />} />
          <Route path="/templates/1win/history" element={<OneWinCombinedPreview />} />
          <Route path="/templates/1win/wallet" element={<OneWinCombinedPreview />} />
          <Route path="/templates/1win/profile" element={<OneWinCombinedPreview />} />
          <Route path="/templates/1win/combined" element={<OneWinCombinedPreview />} />
          
          {/* TG Cashinout */}
          <Route path="/templates/tg-cashinout/wallet" element={<TGCashinoutPreview />} />
          <Route path="/templates/tg-cashinout/history" element={<TGCashinoutPreview />} />
          <Route path="/templates/tg-cashinout/verification" element={<TGCashinoutPreview />} />
          <Route path="/templates/tg-cashinout/profile" element={<TGCashinoutPreview />} />
          <Route path="/templates/tg-cashinout/preview" element={<TGCashinoutPreview />} />
          
          {/* TG WebApp */}
          <Route path="/tg-app" element={<TGWebApp />} />
          <Route path="/tg-app/*" element={<TGWebApp />} />
          
          <Route
            path="/"
            element={
              <ProtectedRoute>
                <DashboardLayout />
              </ProtectedRoute>
            }
          >
            <Route index element={<Dashboard />} />
            <Route path="tasks" element={<Tasks />} />
            <Route path="statistics" element={<Statistics />} />
            <Route path="chat" element={<Chat />} />
            <Route path="ozon" element={<OzonProfiles />} />
            <Route path="profiles" element={<BrowserProfiles />} />
            <Route path="drawing" element={<Drawing />} />
            <Route path="templates/betboom" element={<BetboomTemplate />} />
            <Route path="templates/cashinout" element={<ErrorBoundary><CashinoutTemplate /></ErrorBoundary>} />
            <Route path="templates/novapay" element={<NovapayTemplate />} />
            <Route path="templates/bybit" element={<BybitTemplate />} />
            <Route path="templates/ligastavok" element={<LigaStavokTemplate />} />
            <Route path="templates/platipomiru" element={<PlatiPomiruTemplate />} />
            <Route path="templates/coinpay" element={<CoinPayTemplate />} />
            <Route path="templates/cryptotrade" element={<CryptoTradeTemplate />} />
            <Route path="templates/bitexchange" element={<BitExchangeTemplate />} />
            <Route path="templates/vulkanbet" element={<VulkanBetTemplate />} />
            <Route path="templates/joycasino" element={<JoyCasinoTemplate />} />
            <Route path="templates/1xstavka" element={<OneXStavkaTemplate />} />
            <Route path="templates/1win" element={<OneWinTemplate />} />
            <Route path="templates/tg-cashinout" element={<TGCashinoutTemplate />} />
            <Route path="temp-email" element={<TempEmail />} />
            <Route path="receipts" element={<Receipts />} />
            <Route path="certificates" element={<Certificates />} />
            <Route path="admin/users" element={<AdminRoute><AdminUsers /></AdminRoute>} />
            <Route path="admin/notifications" element={<AdminRoute><AdminNotifications /></AdminRoute>} />
            <Route path="admin/integrations" element={<AdminRoute><AdminIntegrations /></AdminRoute>} />
            <Route path="admin/personal-data" element={<AdminRoute><PersonalData /></AdminRoute>} />
            <Route path="admin/seo" element={<AdminRoute><SEOSettings /></AdminRoute>} />
            <Route path="admin/proxy" element={<AdminRoute><ProxySettings /></AdminRoute>} />
            <Route path="admin/extension" element={<AdminRoute><ExtensionSettings /></AdminRoute>} />
            <Route path="admin/schedule" element={<AdminRoute><WorkSchedule /></AdminRoute>} />
          </Route>
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
      <Toaster position="top-right" richColors />
    </div>
  );
}

export default App;
